import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useMobileDetect } from './dashboard/useMobileDetect';
import DashboardHeader from './dashboard/DashboardHeader';
import DashboardTutorial from './dashboard/DashboardTutorial';
import DashboardLayoutShell from './dashboard/DashboardLayoutShell';
import DashboardModals from './dashboard/DashboardModals';
import DailyMomentCard from './DailyMomentCard';
import { getAvailablePanes } from './dashboard/availablePanes';
import { parseReferenceString } from '../utils/referenceParser';

import { useKeyboardShortcuts, getDefaultShortcuts } from '../hooks/useKeyboardShortcuts';
import { useDashboardPreferences } from '../hooks/useDashboardPreferences';
import { useDashboardSelection } from '../hooks/useDashboardSelection';
import { localizeReference } from '../utils/bookTranslations';

/**
 * Top-level dashboard orchestrator. Kept intentionally thin — most state
 * lives in hooks (`useDashboardPreferences`, `useDashboardSelection`) and
 * rendering is delegated to `DashboardLayoutShell` and `DashboardModals`.
 */
const Dashboard = () => {
  const { user, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { isMobile } = useMobileDetect();

  const {
    layout, layoutGrid, paneWidths,
    saveLayout, saveLayoutGrid, handlePaneResize, removePane,
  } = useDashboardPreferences({ language });

  const {
    selectedVerse, aiInsights, showAIInsights, isLoadingAI,
    setSelectedVerse, handleAIAnalyze,
  } = useDashboardSelection({ language });

  const availablePanes = useMemo(() => getAvailablePanes(language), [language]);

  // Modal open/close state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLayoutCustomizerOpen, setIsLayoutCustomizerOpen] = useState(false);
  const [isPresetLayoutSelectorOpen, setIsPresetLayoutSelectorOpen] = useState(false);
  const [isShortcutsHelpOpen, setIsShortcutsHelpOpen] = useState(false);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  // Listen for `lumera:verse-select` events emitted by the new resource panes
  // (Topical, Atlas, Historic Plans, TSK chips) and route them to the Bible
  // reader by updating `selectedVerse`. Also makes sure the Bible pane is
  // visible — if it isn't in the current layout, drop it onto the first row.
  useEffect(() => {
    const handler = (e) => {
      const ref = e?.detail?.reference;
      const parsed = parseReferenceString(ref);
      if (!parsed) return;
      setSelectedVerse(parsed);
      if (!layout.includes('bible')) {
        if (layoutGrid) {
          const next = layoutGrid.map((row, idx) => (idx === 0 ? ['bible', ...row] : row));
          saveLayoutGrid(next, false);
        } else {
          saveLayout(['bible', ...layout], false);
        }
      }
      toast.success(`${language === 'es' ? 'Abriendo' : 'Opening'} ${localizeReference(parsed.reference, language === 'es')}`);
    };
    window.addEventListener('lumera:verse-select', handler);
    return () => window.removeEventListener('lumera:verse-select', handler);
  }, [layout, layoutGrid, saveLayout, saveLayoutGrid, setSelectedVerse, language]);

  // A standalone tool (e.g. chronology maps) may hand off a verse to open via
  // sessionStorage before navigating here. Re-emit it once mounted so the
  // existing verse-select handler opens the reader on that reference. The key
  // is cleared inside the timeout (not during the effect body) so React 18
  // StrictMode's double-invoke doesn't swallow the pending ref.
  useEffect(() => {
    let ref = null;
    try { ref = sessionStorage.getItem('lumera_pending_verse'); } catch (_) { /* noop */ }
    if (!ref) return undefined;
    const id = setTimeout(() => {
      try { sessionStorage.removeItem('lumera_pending_verse'); } catch (_) { /* noop */ }
      window.dispatchEvent(new CustomEvent('lumera:verse-select', { detail: { reference: ref } }));
    }, 250);
    return () => clearTimeout(id);
  }, []);

  // On mobile, replace layout; on desktop, append.
  const handlePaneSelect = useCallback((paneId) => {
    if (isMobile) {
      saveLayout([paneId], false);
    } else if (!layout.includes(paneId)) {
      // If a grid is set, append to the last row; else append to flat layout.
      if (layoutGrid) {
        const next = layoutGrid.map((row, idx) => (
          idx === layoutGrid.length - 1 ? [...row, paneId] : row
        ));
        saveLayoutGrid(next, false);
      } else {
        saveLayout([...layout, paneId], false);
      }
    }
    setIsMobileMenuOpen(false);
  }, [isMobile, layout, layoutGrid, saveLayout, saveLayoutGrid]);

  // Single-row reorder
  const handlePaneReorder = useCallback((newLayout) => {
    saveLayout(newLayout, false);
    toast.success(language === 'es' ? 'Paneles reordenados' : 'Panes reordered');
  }, [saveLayout, language]);

  // Grid reorder — either replace a single row (legacy: rowIdx is a number)
  // or replace the entire grid (new: rowIdx === 'full', newRow is the new 2-D grid).
  const handlePaneGridReorder = useCallback((rowIdx, newRow) => {
    if (rowIdx === 'full') {
      saveLayoutGrid(newRow, false);
      return;
    }
    const next = layoutGrid.map((row, idx) => (idx === rowIdx ? newRow : row));
    saveLayoutGrid(next, false);
  }, [layoutGrid, saveLayoutGrid]);

  // Keyboard shortcuts
  const paneMap = useMemo(() => ({
    'ctrl+b': 'bible',
    'ctrl+c': 'concordance',
    'ctrl+d': 'dictionary',
    'ctrl+m': 'maps',
    'ctrl+n': 'notes',
    'ctrl+p': 'reading-plans',
    'ctrl+h': 'highlights',
    'ctrl+i': 'companion',
  }), []);

  const shortcuts = useMemo(() => (
    getDefaultShortcuts(language).map((shortcut) => ({
      ...shortcut,
      action: () => {
        if (shortcut.key === '?') {
          setIsShortcutsHelpOpen(true);
        } else if (shortcut.key === 'ctrl+shift+l') {
          setIsPresetLayoutSelectorOpen(true);
        } else if (shortcut.key === 'alt+l') {
          // Language toggle handled by header
        } else {
          const paneId = paneMap[shortcut.key];
          if (paneId) {
            saveLayout([paneId], false);
            toast.success(`${language === 'es' ? 'Abriendo' : 'Opening'} ${shortcut.name}`);
          }
        }
      },
    }))
  ), [language, paneMap, saveLayout]);

  useKeyboardShortcuts(shortcuts);

  return (
    <div className="h-screen flex flex-col bg-background">
      <DashboardHeader
        user={user}
        onMenuToggle={() => setIsMobileMenuOpen(true)}
        onLayoutCustomize={() => setIsPresetLayoutSelectorOpen(true)}
        onLogout={handleLogout}
      />

      <DashboardModals
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        availablePanes={availablePanes}
        layout={layout}
        onPaneSelect={handlePaneSelect}
        onLogout={handleLogout}
        onOpenLayoutCustomizer={() => setIsLayoutCustomizerOpen(true)}
        isLayoutCustomizerOpen={isLayoutCustomizerOpen}
        setIsLayoutCustomizerOpen={setIsLayoutCustomizerOpen}
        saveLayout={saveLayout}
        isPresetLayoutSelectorOpen={isPresetLayoutSelectorOpen}
        setIsPresetLayoutSelectorOpen={setIsPresetLayoutSelectorOpen}
        saveLayoutGrid={saveLayoutGrid}
        language={language}
        isShortcutsHelpOpen={isShortcutsHelpOpen}
        setIsShortcutsHelpOpen={setIsShortcutsHelpOpen}
        shortcuts={shortcuts}
      />

      <main className="flex-1 overflow-hidden flex flex-col">
        <div className="px-4 pt-4">
          <DailyMomentCard language={language} />
        </div>
        {!isMobile && <DashboardTutorial />}

        <DashboardLayoutShell
          isMobile={isMobile}
          layout={layout}
          layoutGrid={layoutGrid}
          availablePanes={availablePanes}
          paneWidths={paneWidths}
          selectedVerse={selectedVerse}
          aiInsights={aiInsights}
          showAIInsights={showAIInsights}
          isLoadingAI={isLoadingAI}
          language={language}
          onPaneResize={handlePaneResize}
          onPaneReorder={handlePaneReorder}
          onPaneGridReorder={handlePaneGridReorder}
          onRemovePane={removePane}
          onVerseChange={setSelectedVerse}
          onAIAnalyze={handleAIAnalyze}
        />
      </main>
    </div>
  );
};

export default Dashboard;
